import { Suspense, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import LabelManager from "../features/labels/components/LabelManager";
import { useProjectContext } from "../hooks/useProjectContext";
import ProjectLocationForm from "../features/projects/components/ProjectLocationForm";
import ProjectDetailForm from "../features/projects/components/ProjectDetailForm";
import ProjectAIConfigForm from "../features/projects/components/ProjectAIConfigForm";
import SettingsCard from "../components/SettingsCard";
import { useToastContext } from "../App";
import { pageShell } from "../styles/pageShell";
import Alert from '../components/Alert';
import Loading from "../features/uiStatus/components/Loading";

const ProjectDetailPage = () => {
    const { projectId } = useProjectContext();
    const { showToast } = useToastContext();
    // DashboardPageからprojectを取得（重複呼び出しを防ぐ）
    const outletContext = useOutletContext();
    const projectFromContext = outletContext?.project;
    const [project, setProject] = useState(projectFromContext || null);
    const [loading, setLoading] = useState(!projectFromContext);

    // DashboardPageからprojectが渡されていない場合のみ取得
    useEffect(() => {
        if (projectFromContext) {
            setProject(projectFromContext);
            setLoading(false);
        }
    }, [projectFromContext]);

    const handleProjectUpdate = (updatedProject) => {
        setProject(updatedProject);
    };

    // 各コンポーネントからのトースト表示用コールバック
    const handleProjectDetailSuccess = () => {
        showToast('プロジェクトが正常に更新されました', 'success');
    };

    const handleProjectDetailError = () => {
        showToast('プロジェクトの更新に失敗しました', 'error');
    };

    const handleLocationSuccess = () => {
        showToast('マップ設定が正常に更新されました', 'success');
    };

    const handleLocationError = () => {
        showToast('マップ設定の更新に失敗しました', 'error');
    };

    const handlePublicModeSuccess = (isPublic, updatedProject) => {
        // プロジェクト状態を更新してバッジをリアルタイム反映
        if (updatedProject) {
            setProject(updatedProject);
        } else {
            // updatedProjectが渡されない場合は、is_publicのみ更新
            setProject(prev => prev ? { ...prev, is_public: isPublic } : null);
        }
        showToast(
            isPublic ? '公開モードを有効にしました' : '公開モードを無効にしました',
            'success'
        );
    };

    const handlePublicModeError = () => {
        showToast('公開モードの更新に失敗しました', 'error');
    };

    const handleUrlCopySuccess = () => {
        showToast('URLをクリップボードにコピーしました', 'success');
    };

    const handleUrlCopyError = () => {
        showToast('URLのコピーに失敗しました', 'error');
    };

    const handleLabelSuccess = (action) => {
        const messages = {
            add: 'ラベルが正常に追加されました',
            edit: 'ラベルが正常に更新されました',
            delete: 'ラベルが正常に削除されました'
        };
        showToast(messages[action] || '操作が完了しました', 'success');
    };

    const handleLabelError = (action) => {
        const messages = {
            add: 'ラベルの追加に失敗しました',
            edit: 'ラベルの更新に失敗しました',
            delete: 'ラベルの削除に失敗しました'
        };
        showToast(messages[action] || '操作に失敗しました', 'error');
    };

    const handleAIConfigSuccess = () => {
        showToast('AI設定が正常に更新されました', 'success');
    };

    const handleAIConfigError = () => {
        showToast('AI設定の更新に失敗しました', 'error');
    };

    if (!projectId) {
        return (
            <div className={pageShell.container}>
                <div className={pageShell.card}>
                    <Alert type="danger" message="プロジェクトが見つかりません。" />
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className={pageShell.container}>
                <div className={pageShell.card}>
                    <Loading isLoading />
                </div>
            </div>
        );
    }

    return (
        <div className={pageShell.containerAuto}>
            <div className={pageShell.header}>
                <h2 className={pageShell.title}>プロジェクト設定</h2>
            </div>

            <div className="flex w-full flex-col gap-section bp-lg:gap-8">
                <Suspense fallback={<SettingsCard loading={true} title="読み込み中..." />}>
                    <SettingsCard
                        icon="settings"
                        title="プロジェクト情報"
                        description="プロジェクトの基本情報、サムネイル、説明、公開設定を管理します"
                        headerAside={
                            project ? (
                                project.is_public ? (
                                    <span className={`${pageShell.statusBadge} border-success-30 bg-success-15 text-success`}>
                                        <span className={pageShell.badgeIcon}>public</span>
                                        公開中
                                    </span>
                                ) : (
                                    <span className={`${pageShell.statusBadge} border-border bg-border-subtle text-text-hint`}>
                                        <span className={pageShell.badgeIcon}>lock</span>
                                        非公開
                                    </span>
                                )
                            ) : null
                        }
                    >
                        <ProjectDetailForm
                            projectId={projectId}
                            project={project}
                            onUpdate={handleProjectUpdate}
                            onSuccess={handleProjectDetailSuccess}
                            onError={handleProjectDetailError}
                            onPublicModeSuccess={handlePublicModeSuccess}
                            onPublicModeError={handlePublicModeError}
                            onUrlCopySuccess={handleUrlCopySuccess}
                            onUrlCopyError={handleUrlCopyError}
                        />
                    </SettingsCard>
                </Suspense>

                <Suspense fallback={<SettingsCard loading={true} title="読み込み中..." />}>
                    <SettingsCard
                        icon="edit_location_alt"
                        title="マップ設定"
                        description="地図の初期表示位置とズームレベルを設定します"
                    >
                        <ProjectLocationForm
                            projectId={projectId}
                            onSuccess={handleLocationSuccess}
                            onError={handleLocationError}
                        />
                    </SettingsCard>
                </Suspense>

                <Suspense fallback={<SettingsCard loading={true} title="読み込み中..." />}>
                    <SettingsCard
                        icon="label"
                        title="ラベル管理"
                        description="プロジェクトで使用するラベルを追加・編集・削除します"
                    >
                        <LabelManager
                            projectId={projectId}
                            onSuccess={handleLabelSuccess}
                            onError={handleLabelError}
                        />
                    </SettingsCard>
                </Suspense>

                <Suspense fallback={<SettingsCard loading={true} title="読み込み中..." />}>
                    <SettingsCard
                        icon="smart_toy"
                        title="AI設定"
                        description="AI会話機能のモデルタイプとデフォルトパラメータを設定します"
                    >
                        <ProjectAIConfigForm
                            projectId={projectId}
                            onSuccess={handleAIConfigSuccess}
                            onError={handleAIConfigError}
                        />
                    </SettingsCard>
                </Suspense>
            </div>
        </div>
    );
};

export default ProjectDetailPage;
